import React, { useState, useEffect } from 'react';
import { Search, Trash2, UserX, Plus, X, User } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { RegisterFormFields, validateRegisterForm } from '../pages/Auth';
import { api } from '../utils/api';
import Pagination from '../components/Pagination';
import ConfirmModal from '../components/ConfirmModal';

// ── Add User Modal — reuses the exact same Register form fields ───────────────
function AddUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', dob: '', password: '', confirm: '', role: 'user',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const set = (k: string) => (v: string) => setForm(p => ({ ...p, [k]: v }));

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateRegisterForm(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);

    try {
      await api.post('/admin/users', form);
      toast('User created successfully!', 'success');
      onSuccess();
      onClose();
    } catch (e: any) {
      setErrors({ form: e.message || 'Failed to create user' });
      toast(e.message || 'Failed to create user', 'error');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card w-full max-w-[520px]">

        {/* Header — mirrors the auth card branding */}
        <div className="flex items-center justify-between p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-[var(--glass-border)] shrink-0 bg-[rgba(95,156,76,0.1)]">
              <User className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
            </div>
            <div>
              <p className="text-sm font-black tracking-tight text-[var(--color-text)]">Add New User</p>
              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[var(--color-muted)]">Admin · Stone Bench</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {errors.form && (
          <div className="mb-4 p-3 rounded-xl text-[13px] font-medium text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
            {errors.form}
          </div>
        )}

        <form onSubmit={handle} className="space-y-4 max-h-[70vh] overflow-y-auto pl-6 pr-6 pb-6" noValidate>
          <RegisterFormFields form={form} errors={errors} set={set} />

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost rounded-xl">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="btn btn-primary flex-1 py-3 rounded-xl text-sm disabled:opacity-60">
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

// ── Admin Users page ──────────────────────────────────────────────────────────
export default function AdminUsers() {
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; email: string } | null>(null);
  const { toast } = useToast();

  const fetchUsers = (page = 1) => {
    setLoading(true);
    // Use limit=5 as requested
    api.get(`/admin/users?page=${page}&limit=5&search=${search}`)
      .then((res: any) => {
        setUsers(res.data || []);
        setPagination(res.pagination);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchUsers(1); }, []);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    // In a real app with server-side search, we'd fetch page 1 here.
    // Since search is still client-side for now, we don't need to refetch,
    // but the serial number logic might need adjustment if we want it to be per-page.
  };

  const handleDelete = async (id: number, email: string) => {
    setConfirmDelete({ id, email });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    await api.delete(`/admin/users/${confirmDelete.id}`);
    toast('User deleted.', 'info');
    setConfirmDelete(null);
    fetchUsers();
  };

  const handleBlock = async (id: number, blocked: boolean) => {
    await fetch(`/api/admin/users/${id}/block`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blocked: !blocked }),
    });
    toast(blocked ? 'User unblocked.' : 'User blocked.', 'info');
    fetchUsers();
  };

  const filtered = users.filter(u =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {confirmDelete && (
        <ConfirmModal
          title="Are you sure?"
          message={`You are about to delete user "${confirmDelete.email}". This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={executeDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchUsers}
        />
      )}

      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">User Management</h1>
            <p className="text-sm text-[var(--color-muted)]">{users.length} registered users</p>
          </div>
          <div className="flex gap-2 self-start sm:self-auto">
            <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm">
              <Plus className="w-3.5 h-3.5" /> Add New User
            </button>
          </div>
        </div>

        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: 'var(--color-muted)' }} />
          <input className="input !pl-[30px]" placeholder="Search by name or email…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="card">
          {loading ? (
            <div className="py-16 text-center text-sm text-[var(--color-muted)]">Loading users…</div>
          ) : (
            <div className="p-4 sm:p-0">
              {/* Mobile card layout */}
              <div className="sm:hidden space-y-3">
                {filtered.map((u, i) => (
                  <div key={u.id} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-[var(--color-text)]">{u.name || '—'}</p>
                        <p className="text-[10px] text-[var(--color-muted)] truncate">{u.email}</p>
                        <p className="text-[10px] text-[var(--color-muted)]">{u.phone || '—'}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2">
                        <span className="text-[10px] text-[var(--color-muted)]">#{(pagination.page - 1) * 5 + i + 1}</span>
                        <span className={`badge ${u.role === 'admin' ? 'badge-purple' : 'badge-blue'}`}>
                          {u.role}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`badge ${u.blocked ? 'badge-red' : 'badge-green'}`}>
                          {u.blocked ? '● Blocked' : '● Active'}
                        </span>
                        <span className="text-[10px] text-[var(--color-muted)]">
                          {new Date(u.created_at).toISOString().slice(0, 10)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleBlock(u.id, !!u.blocked)}
                          className="btn btn-icon btn-ghost"
                          title={u.blocked ? 'Unblock user' : 'Block user'}>
                          <UserX className={`w-3.5 h-3.5 ${u.blocked ? 'text-[var(--color-accent)]' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleDelete(u.id, u.email)}
                          className="btn btn-icon btn-danger"
                          title="Delete user">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div className="text-center text-[var(--color-muted)] py-8 text-sm">No users found</div>
                )}
              </div>

              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>S.No</th><th>Name</th><th>Email</th><th>Phone</th>
                      <th>Role</th><th>Registered</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u, i) => (
                      <tr key={u.id}>
                        <td className="text-[var(--color-muted)] font-mono text-xs">{(pagination.page - 1) * 5 + i + 1}</td>
                        <td className="font-medium">{u.name || '—'}</td>
                        <td className="max-w-[160px] truncate text-xs">{u.email}</td>
                        <td className="text-xs">{u.phone || '—'}</td>
                        <td>
                          <span className={`badge ${u.role === 'admin' ? 'badge-purple' : 'badge-blue'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="text-[var(--color-muted)] text-xs">
                          {new Date(u.created_at).toISOString().slice(0, 10)}
                        </td>
                        <td>
                          <span className={`badge ${u.blocked ? 'badge-red' : 'badge-green'}`}>
                            {u.blocked ? '● Blocked' : '● Active'}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleBlock(u.id, !!u.blocked)}
                              className="btn btn-icon btn-ghost"
                              title={u.blocked ? 'Unblock user' : 'Block user'}>
                              <UserX className={`w-3.5 h-3.5 ${u.blocked ? 'text-[var(--color-accent)]' : ''}`} />
                            </button>
                            <button
                              onClick={() => handleDelete(u.id, u.email)}
                              className="btn btn-icon btn-danger"
                              title="Delete user">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center text-[var(--color-muted)] py-10">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchUsers}
      />
    </>
  );
}
